/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import classNames from 'classnames';
import { UseControllerProps, Control, useController } from 'react-hook-form';
import styles from './Toggle.module.scss';

export interface ToggleProps {
  checked?: boolean;
  label?: string;
  disabled?: boolean;
  /**
   * Non-interactive status indicator: keeps the full on/off colors (unlike
   * `disabled`, which greys the slider) but ignores clicks.
   */
  readOnly?: boolean;
  onToggle?: (isToggled: boolean) => void;
  horizontalLabel?: boolean;
}

const Toggle: React.FC<ToggleProps> = ({
  onToggle,
  checked = false,
  label,
  disabled = false,
  readOnly = false,
  horizontalLabel = false,
}) => {
  const handleToggle = () => {
    if (onToggle) {
      onToggle(!checked);
    }
  };

  return (
    <div
      className={classNames(
        styles.root,
        'flex',
        'gap-12',
        { 'flex-col': !horizontalLabel },
        { [styles.disabled]: disabled },
      )}>
      {label && <span className="typo-text-14--bold text-faded">{label}</span>}
      <label
        className={classNames(styles.switch, {
          [styles.switchDisabled]: disabled,
          [styles.switchReadOnly]: readOnly && !disabled,
        })}>
        <input disabled={disabled || readOnly} type="checkbox" checked={checked} onChange={handleToggle} />
        <span className={styles.slider} />
      </label>
    </div>
  );
};

export interface ControlledToggleProps extends Omit<ToggleProps, 'checked' | 'onToggle'>, UseControllerProps {
  name: string; // Required for react-hook-form
  control: Control<any>; // Ensures compatibility with react-hook-form
}

export const ControlledToggle: React.FC<ControlledToggleProps> = ({
  name,
  control,
  rules,
  defaultValue = false,
  label,
  disabled,
  horizontalLabel,
}) => {
  const {
    field: { value, onChange },
  } = useController({ name, control, rules, defaultValue });

  return (
    <Toggle
      checked={value} // Use react-hook-form value
      onToggle={onChange} // Call react-hook-form's onChange
      label={label}
      disabled={disabled}
      horizontalLabel={horizontalLabel}
    />
  );
};

export default Toggle;
